import { Injectable, inject } from '@angular/core';
import { AppState } from '@next/arable-lib/advice';
import { NotificationService, NotificationServiceInjector } from '@next/core-lib/notification';
import { createEffect } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { map, of } from 'rxjs';
import { debounceTime, switchMap, withLatestFrom } from 'rxjs/operators';

import { selectAutomaticAdvice } from 'src/app/advices/store/selectors';
import { selectCropYear } from 'src/app/store/selectors';

import {
  loadAutomaticAdviceSuccess,
  loadCropFieldAdvices,
  loadCustomerOrderPlan,
  loadOrderLines,
} from '../../../advices/store/actions';
import { GetAutomaticAdviceResponse } from '../../models';
import {
  AutomaticAdviceCropFieldDoneEvent,
  AutomaticAdviceDoneEvent,
  AutomaticAdviceEvents,
} from '../automatic-advice.models';

@Injectable()
export class AutomaticAdviceNotificationEffects {
  private readonly notification = inject<NotificationService>(NotificationServiceInjector);
  private readonly store = inject<Store<AppState>>(Store);

  automaticAdviceDone$ = createEffect(() =>
    this.notification
      .on<AutomaticAdviceDoneEvent>(AutomaticAdviceEvents.automaticAdviceDone)
      .pipe(
        withLatestFrom(this.store.select(selectAutomaticAdvice), this.store.select(selectCropYear)),
        switchMap(([event, automaticAdvice, cropYear]) => {
          const updatedAutomaticAdvice: GetAutomaticAdviceResponse = {
            customerId: event.customerId,
            cropYear: event.year,
            state: 'ReadyToOrder',
            cropFieldIds: automaticAdvice?.cropFieldIds || [],
            completedCropFieldIds: automaticAdvice?.completedCropFieldIds || [],
          };
          return of(
            loadAutomaticAdviceSuccess({ automaticAdvice: updatedAutomaticAdvice }),
            loadCropFieldAdvices({ cropYear }),
            loadOrderLines({ shouldOpenOrderPlanSummary: false }),
            loadCustomerOrderPlan(),
          );
        }),
      ),
  );

  automaticAdviceCropFieldDone$ = createEffect(() =>
    this.notification
      .on<AutomaticAdviceCropFieldDoneEvent>(AutomaticAdviceEvents.automaticAdviceCropFieldDone)
      .pipe(
        debounceTime(3000),
        map((event) => (typeof event === 'string' ? JSON.parse(event) : event) as AutomaticAdviceCropFieldDoneEvent),
        withLatestFrom(this.store.pipe(select(selectAutomaticAdvice)), this.store.select(selectCropYear)),
        switchMap(([event, automaticAdvice, cropYear]) => {
          if (automaticAdvice?.state === 'ReadyToOrder') {
            return of();
          }

          const updatedCompletedCropFieldIds = [...(automaticAdvice?.completedCropFieldIds || []), event.CropFieldId];
          const updatedAutomaticAdvice: GetAutomaticAdviceResponse = {
            customerId: event.CustomerId,
            cropYear: event.Year,
            state: updatedCompletedCropFieldIds.length === (automaticAdvice?.cropFieldIds.length || 0) ? 'ReadyToOrder' : 'Triggered',
            cropFieldIds: automaticAdvice?.cropFieldIds || [],
            completedCropFieldIds: updatedCompletedCropFieldIds,
          };
          return of(
            loadAutomaticAdviceSuccess({ automaticAdvice: updatedAutomaticAdvice }),
            loadCropFieldAdvices({ cropYear }),
          );
        }),
      ),
  );
}
